import { useState } from 'react'

import { 
  Divider, 
  EmptyPlaceholder, 
  LoadingPlaceholder, 
  SearchInput 
} from "@components"

import { ChevronLeftIcon, CircleStackIcon } from '@heroicons/react/24/outline'

import { 
  Button, 
  Chip, 
  Pagination 
} from '@heroui/react'

import ErrorLoadingPlaceholder from '../ErrorLoadingPlaceholder'
import PropTypes from 'prop-types'

const StatDetails = ({ 
  currentPage, 
  currentView, 
  emails,
  error,
  handleChangePage,
  handleSearchChange,
  handleClose,
  isLoading, 
  paginatorInfo,
  showPagination
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  
  const handleChange = (searchTerm) => {
    setSearchTerm(searchTerm)
    handleSearchChange(searchTerm)
  }

  const handleClear = () => {
    setSearchTerm("")
    handleSearchChange("")
  }

  if (error) {
    return (
      <div>
        <ErrorLoadingPlaceholder />

        <Button color="secondary" size="lg" className='mt-6' fullWidth onPress={() => handleClose()}>
          <ChevronLeftIcon className='w-6 h-6' /> Back to Summary
        </Button>
      </div>
    )
  }

  return (
    <div>
      <h2 className='mb-2 text-lg font-semibold'>Detailed Email Stats - {currentView}</h2>

      <SearchInput
        classNames="mb-4"
        debounceMilliSeconds={300}
        placeholder="Search by email address"
        handleChange={(term) => handleChange(term)}
        handleClear={() => handleClear()}
        value={searchTerm}
      />
      
      <div>
        {isLoading && <LoadingPlaceholder icon={CircleStackIcon} caption="Loading delivery stats..." />}
        
        {!isLoading && emails.length === 0 && (
          <EmptyPlaceholder className="mt-3" title="No emails found" description="There are no email addresses that match your search">
            {searchTerm.length > 0 && (
              <Button color="primary" size="md" className='mt-4' fullWidth onPress={handleClear}>
                Clear Filters
              </Button>
            )}
          </EmptyPlaceholder>
        )}

        {!isLoading && emails.length > 0 && (
          <div className='mt-5'>            
            { emails.map(details => {
              const color = details.status.is_delivered 
                ? "success" : details.status.is_failed 
                ? "danger" :  details.status.is_unknown 
                ? "warning" : "primary";
              
              return (
                <div key={details.id}>
                  <div className='flex items-center justify-between pb-3 gap-x-2'>
                    <span className='text-sm'>{details.email}</span>
                    <Chip size="sm" className='text-white' color={color}>{details.status.name}</Chip>
                  </div>
                  
                  <Divider className='mb-2' />
                </div>
              )
            })}
      
            {showPagination && (
              <div className='flex items-center justify-between'>
                <p className="font-semibold">Showing {currentPage} of {paginatorInfo.total}</p>
                
                <Pagination
                  showControls
                  showShadow
                  color="secondary"
                  page={currentPage}
                  total={paginatorInfo?.lastPage}
                  onChange={page => handleChangePage(page)}
                />
              </div>
            )}
          </div>
        )}

        <Button color="secondary" size="md" className='mt-6' fullWidth onPress={() => handleClose()}>
          <ChevronLeftIcon className='w-6 h-6' /> Back to Summary
        </Button>
      </div>
    </div>
  )
}

StatDetails.propTypes = {
  currentPage: PropTypes.number.isRequired,
  currentView: PropTypes.string.isRequired,
  emails: PropTypes.array.isRequired,
  error: PropTypes.object,
  handleChangePage: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSearchChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  paginatorInfo: PropTypes.object.isRequired,
  showPagination: PropTypes.bool.isRequired
}

export default StatDetails